import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Champ date stylisé pour les wizards.
 * - Affiche la date sélectionnée au format "Vendredi 8 Mai. 2026"
 * - Ouvre un calendrier au clic (popover en dessous)
 * - Couleur thème : purple | blue | green | red | orange | indigo
 *
 * Props:
 *   value: Date | null
 *   onChange: (Date | null) => void
 *   label: string (optionnel — affiche un label en uppercase au-dessus)
 *   theme: string (default 'blue')
 *   testid: string (optionnel)
 *   placeholder: string (default "Sélectionner une date")
 */
const THEMES = {
  purple: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-600', borderHover: 'hover:border-purple-400', selectedBg: 'bg-purple-500' },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-600',   borderHover: 'hover:border-blue-400',   selectedBg: 'bg-blue-500' },
  green:  { bg: 'bg-emerald-50',border: 'border-emerald-300',text: 'text-emerald-600',borderHover: 'hover:border-emerald-400',selectedBg: 'bg-emerald-500' },
  red:    { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-600',    borderHover: 'hover:border-red-400',    selectedBg: 'bg-red-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-600', borderHover: 'hover:border-orange-400', selectedBg: 'bg-orange-500' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-600', borderHover: 'hover:border-indigo-400', selectedBg: 'bg-indigo-500' },
};

const WizardDateField = ({
  value,
  onChange,
  label,
  theme = 'blue',
  testid,
  placeholder = 'Sélectionner une date',
}) => {
  const t = THEMES[theme] || THEMES.blue;
  const safeValue = (() => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  })();

  const [open, setOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(safeValue || new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    if (open && safeValue) setCalendarMonth(safeValue);
  }, [open, safeValue]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);
  const blanks = (startDayOfWeek + 6) % 7; // make Monday first

  const handleSelect = (d) => {
    // Set to noon local to avoid timezone day-shift bug
    const safe = new Date(d.getTime());
    safe.setHours(12, 0, 0, 0);
    onChange(safe);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        className={`w-full p-3 ${t.bg} border-2 ${t.border} ${t.borderHover} rounded-xl flex items-center justify-between gap-2 transition-colors text-left`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <CalendarIcon className={`${t.text} flex-shrink-0`} size={20} />
          <span className={`truncate ${safeValue ? 'text-gray-900 font-semibold capitalize' : 'text-gray-400 italic'}`}>
            {safeValue ? format(safeValue, 'EEEE d MMM. yyyy', { locale: fr }) : placeholder}
          </span>
        </span>
        <span className="flex items-center gap-1">
          {safeValue && (
            <span
              role="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 text-xs px-1"
              title="Effacer"
            >
              ✕
            </span>
          )}
          <ChevronRight className={`${t.text}`} size={18} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl p-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-800 capitalize">
              {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
            </span>
            <button
              type="button"
              onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((d) => (
              <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: blanks }).map((_, i) => <div key={`b-${i}`} />)}
            {days.map((day) => {
              const isSelected = safeValue && format(day, 'yyyy-MM-dd') === format(safeValue, 'yyyy-MM-dd');
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
              return (
                <button
                  key={day.toString()}
                  type="button"
                  onClick={() => handleSelect(day)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    isSelected
                      ? `${t.selectedBg} text-white`
                      : isToday
                      ? `${t.bg} ${t.text}`
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardDateField;
